import React from "react";
import { IEvidence, EvidenceAttachment, EvidenceStandardTag, EvidenceCustomTag } from "@interfaces/Interfaces";
import EvidenceTitle from "./EvidenceTitle";
import EvidenceBlock from "./EvidenceBlock";
import EvidenceDate from "./EvidenceDate";
import * as StringUtils from "@utils/stringUtils";
import DataController from "@controllers/DataController";
import { IonSpinner } from "@ionic/react";
import classNames from "classnames";

interface StandardEvidenceContentProps {
  evidence: IEvidence;
  viewingAttachment: boolean;
  viewingAttachmentFilename: string;
  onViewAttachment: (attachment: EvidenceAttachment) => void;
}

const StandardEvidenceContent: React.FC<StandardEvidenceContentProps> = ({
  evidence,
  viewingAttachment,
  viewingAttachmentFilename,
  onViewAttachment,
}) => {
  const hasEvidenceJSONTitle = evidence.evidenceJSON?.Title;

  return (
    <>
      <EvidenceTitle title={hasEvidenceJSONTitle ? evidence.evidenceJSON?.Title : evidence.title} />

      {evidence.activityDescription && (
        <EvidenceBlock
          header="Activity description"
          textContent={evidence.activityDescription}
        />
      )}

      <EvidenceDate
        date={evidence.date}
        label="Date of reflection"
      />

      {evidence.activity && (
        <EvidenceBlock
          header="CPD Activity"
          textContent={evidence.activity.type}
        />
      )}

      {evidence.activityDate && (
        <EvidenceBlock
          header="Date of activity"
          textContent={StringUtils.getActivityDateString(evidence.activityDate, evidence.activityDateEnd)}
        />
      )}

      {evidence.numberOfHours && (
        <EvidenceBlock
          header="Number of hours"
          textContent={StringUtils.getActivityDurationDisplayString(
            DataController.convertToHoursMinutes(evidence.numberOfHours)
          )}
        />
      )}

      {evidence.standardTags && evidence.standardTags.length > 0 && (
        <EvidenceBlock
          header="National Occupational Standards"
          textContent={evidence.standardTags
            .map((tag: EvidenceStandardTag) => tag.tag)
            .sort()
            .join(", ")}
        />
      )}

      {evidence.customTags && evidence.customTags.length > 0 && (
        <EvidenceBlock
          header="Custom tags"
          textContent={evidence.customTags
            .map((tag: EvidenceCustomTag) => tag.tag)
            .sort()
            .join(", ")}
        />
      )}

      {evidence.attachments && evidence.attachments.length > 0 && (
        <EvidenceBlock header="Attachments">
          <div className="readEvidenceText">
            {evidence.attachments.map((attachment: EvidenceAttachment) => {
              const disabled = viewingAttachment && viewingAttachmentFilename !== attachment.name;
              const viewing = viewingAttachment && viewingAttachmentFilename === attachment.name;

              return (
                <div
                  key={attachment.name}
                  id={attachment.name}
                  className={classNames("readEvidenceAttachmentContainer", {
                    readEvidenceAttachmentContainerWait: viewing,
                    readEvidenceAttachmentContainerDisabled: disabled,
                  })}
                  onClick={() => !(disabled || viewing) && onViewAttachment(attachment)}
                >
                  <div className="readEvidenceAttachment">{attachment.name}</div>
                  {viewing && (
                    <div className="readEvidenceSpinnerContainer">
                      <IonSpinner className="readEvidenceAttachmentIconSpinner" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </EvidenceBlock>
      )}

      {evidence.reflections && (
        <EvidenceBlock
          header="Reflection"
          htmlContent={evidence.reflections}
        />
      )}

      {evidence.benefits && (
        <EvidenceBlock
          header="Benefits"
          htmlContent={evidence.benefits}
        />
      )}

      {evidence.objectives && (
        <EvidenceBlock
          header="Learning / Exercise Objectives"
          htmlContent={evidence.objectives}
        />
      )}

      <EvidenceBlock
        header="Included in audit documents"
        textContent={evidence.addToHCPCAudit ? "Yes" : "No"}
        showDivider={false}
      />
    </>
  );
};

export default StandardEvidenceContent;
